
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Phone, Clock, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { useAttentionRequiredRentals } from '@/hooks/useAttentionRequiredRentals';
import { AttentionRequiredRental } from '@/utils/attention-required-api';
import { Button } from '@/components/ui/button';

interface ExpiringRentalsPanelProps {
  onRentalClick?: (rentalId: string) => void;
  days?: number;
}

export function ExpiringRentalsPanel({ onRentalClick, days = 7 }: ExpiringRentalsPanelProps) {
  const { 
    rentals, 
    isLoading, 
    isError, 
    error,
    refetch,
    lastUpdated
  } = useAttentionRequiredRentals({ days });

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return 'Не указана';
      
      const date = new Date(dateString);
      // Проверка на валидность даты
      if (isNaN(date.getTime())) {
        return 'Неверная дата';
      }
      
      return date.toLocaleDateString('ru-RU');
    } catch (e) {
      return 'Неверная дата';
    }
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return '—';
    
    // Простое форматирование для российских номеров телефонов
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `+${cleaned[0]} (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7, 9)}-${cleaned.substring(9, 11)}`;
    }
    return phone;
  };

  const getStatusBadge = (rental: AttentionRequiredRental) => {
    if (rental.isOverdue) {
      return <Badge variant="destructive" className="flex items-center">
        <XCircle className="h-3 w-3 mr-1" />Просрочена
      </Badge>;
    }
    if (rental.status === 'pending') {
      return <Badge variant="info" className="flex items-center bg-blue-500 hover:bg-blue-600">
        <Clock className="h-3 w-3 mr-1" />Ожидает
      </Badge>;
    }
    return <Badge variant="warning" className="flex items-center bg-amber-500 hover:bg-amber-600">
      <AlertTriangle className="h-3 w-3 mr-1" />Скоро истекает
    </Badge>;
  };

  const getErrorMessage = (error: unknown): string => {
    let errorMessage = 'Не удалось загрузить список арендаторов';
    
    if (error instanceof Error) {
      errorMessage = `${errorMessage}: ${error.message}`;
    } else if (typeof error === 'object' && error !== null) {
      errorMessage = `${errorMessage}: ${JSON.stringify(error)}`;
    }
    
    return errorMessage;
  };

  // Отображаем загрузку, ошибку или данные
  if (isLoading && rentals.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mb-4 p-4 border border-red-200 bg-red-50 rounded text-red-800">
        <p className="font-medium">Ошибка загрузки данных:</p>
        <p className="text-sm mt-1">{getErrorMessage(error)}</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-2" 
          onClick={() => refetch()}
        >
          Попробовать снова
        </Button>
      </div>
    );
  }

  if (rentals.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Нет арендаторов, требующих внимания
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between mb-4">
        <div className="flex items-center mr-4">
          <AlertTriangle className="h-5 w-5 mr-2 text-destructive" />
          <h3 className="text-lg font-semibold">Требуют внимания</h3>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex space-x-2">
            <span className="bg-destructive/10 text-destructive text-xs px-2 py-1 rounded-full flex items-center">
              <span className="h-2 w-2 bg-destructive rounded-full mr-1"></span>
              Просроченные
            </span>
            <span className="bg-blue-500/10 text-blue-500 dark:text-blue-400 text-xs px-2 py-1 rounded-full flex items-center">
              <span className="h-2 w-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-1"></span>
              Ожидают
            </span>
            <span className="bg-amber-500/10 text-amber-500 dark:text-amber-400 text-xs px-2 py-1 rounded-full flex items-center">
              <span className="h-2 w-2 bg-amber-500 dark:bg-amber-400 rounded-full mr-1"></span>
              Скоро истекают
            </span>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => refetch()} 
            disabled={isLoading}
            className="ml-auto"
            title="Обновить данные"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
          {lastUpdated && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {lastUpdated.toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </span>
          )}
        </div>
      </div>
      
      {isLoading && rentals.length > 0 && (
        <div className="absolute right-4 top-4">
          <Badge variant="outline" className="bg-primary/10">
            <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> 
            Обновление...
          </Badge>
        </div>
      )}
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Номер аренды</TableHead>
            <TableHead>Курьер</TableHead>
            <TableHead>Телефон</TableHead>
            <TableHead>Дата окончания</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead>Дней</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rentals.map((rental) => (
            <TableRow 
              key={rental.id}
              className={onRentalClick ? "cursor-pointer hover:bg-muted/50" : ""}
              onClick={() => onRentalClick && onRentalClick(rental.id)}
            >
              <TableCell className="font-medium">{rental.human_number || rental.id.substring(0, 8)}</TableCell>
              <TableCell>{rental.courierName}</TableCell>
              <TableCell>
                <div className="flex items-center">
                  {rental.phone && <Phone className="h-3 w-3 mr-1" />}
                  {formatPhone(rental.phone)}
                </div>
              </TableCell>
              <TableCell>
                {formatDate(rental.expectedEndDate)}
              </TableCell>
              <TableCell>
                {getStatusBadge(rental)}
              </TableCell>
              <TableCell>{rental.daysLeft !== undefined ? rental.daysLeft : '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
